// library imports
import { useState, useEffect, useContext } from "react";
import { Card, Col, Row, Button, Flex, DatePicker, Grid, Carousel } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

// hook imports
import useFetch from "../../hooks/useFetch";

// context imports
import { IndexContext } from "../../context/IndexContext";

// component imports
import { YoYCard } from "../../components/dashboard/YoYCard";
import { OverviewCard } from "../../components/dashboard/OverviewCard";
import { PerformanceCard } from "../../components/dashboard/PerformanceCard";
import { BreakdownCard } from "../../components/dashboard/BreakdownCard";
import { RankingCard } from "../../components/dashboard/RankingCard";
import { RECCard } from "../../components/dashboard/RECCard";

// constant imports
import { GRID_EMISSION_FACTOR } from "../../configs/configs";

const { useBreakpoint } = Grid;

function DashboardPage() {
  const { notificationApi, siteSelected } = useContext(IndexContext);
  const [fetchWrapper] = useFetch();

  const screens = useBreakpoint();

  /** Inventory year selected by user */
  const [inventoryYear, setInventoryYear] = useState(dayjs().startOf("year"));
  const [inventoryYearFilter, setInventoryYearFilter] = useState(
    dayjs().startOf("year"),
  );
  /** Base year selected by user */
  const [baseYear, setBaseYear] = useState(
    dayjs().startOf("year").subtract(1, "year"),
  );
  const [baseYearFilter, setBaseYearFilter] = useState(
    dayjs().startOf("year").subtract(1, "year"),
  );
  /** Filter is pressed */
  const [filterPressed, setFilterPressed] = useState(true);

  // loading states
  const [emissionDataLoading, setEmissionDataLoading] = useState(true);
  const [emissionDataSummaryLoading, setEmissionSummaryLoading] =
    useState(true);
  const [abatementDataLoading, setAbatementDataLoading] = useState(true);
  const [inventoryRevenueLoading, setInventoryRevenueLoading] = useState(true);
  const [baseRevenueLoading, setBaseRevenueLoading] = useState(true);
  const [emissionGoalLoading, setEmissionGoalLoading] = useState(true);
  const [intensityGoalLoading, setIntensityGoalLoading] = useState(true);
  const [revenueTargetLoading, setRevenueTargetLoading] = useState(true);
  const [inventoryTonneProductLoading, setInventoryTonneProductLoading] =
    useState(true);
  const [inventoryPieceProductLoading, setInventoryPieceProductLoading] =
    useState(true);

  const loading =
    emissionDataLoading || emissionDataSummaryLoading || abatementDataLoading;

  const [inventoryRevenue, setInventoryRevenue] = useState(0);
  const [baseRevenue, setBaseRevenue] = useState(0);

  const [inventoryTonneProduct, setInventoryTonneProduct] = useState(0);
  const [inventoryPieceProduct, setInventoryPieceProduct] = useState(0);

  const [emissionGoal, setEmissionGoal] = useState(0);
  const [intensityGoal, setIntensityGoal] = useState(0);
  const [revenueTarget, setRevenueTarget] = useState(0);

  const [netEmission, setNetEmission] = useState(0);
  const [netBaseEmission, setNetBaseEmission] = useState(0);

  const [totalEmissionCalculated, setTotalEmissionCalculated] =
    useState(0); /** Summed emission for inventory year */
  const [totalAbatementCalculated, setTotalAbatementCalculated] = useState(0);

  const [emissionScopeData, setEmissionScopeData] = useState([]);
  const [emissionSummary, setEmissionSummary] = useState([]);

  const [abatementScopeData, setAbatementScopeData] = useState([]);
  const [abatementSummary, setAbatementSummary] = useState([]);

  const onFilter = () => {
    setFilterPressed(!filterPressed);
  };

  /** API to get inventory year revenue */
  function getInventoryYearRevenue() {
    setInventoryRevenueLoading(true);
    fetchWrapper({
      endpoint_url: "dashboard/getRevenue",
      method: "GET",
      params: {
        year: inventoryYear.format("YYYY"),
        site_ID: siteSelected,
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        setInventoryRevenue(response.data.revenue);
        setInventoryRevenueLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setInventoryRevenueLoading(false);
      },
    });
  }

  /** API to get inventory year tonne product */
  function getInventoryYearTonneProduct() {
    setInventoryTonneProductLoading(true);
    fetchWrapper({
      endpoint_url: "dashboard/getTonneProduct",
      method: "GET",
      params: {
        year: inventoryYear.format("YYYY"),
        site_ID: siteSelected,
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        setInventoryTonneProduct(response.data.tonne_product);
        setInventoryTonneProductLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setInventoryTonneProductLoading(false);
      },
    });
  }

  /** API to get inventory year piece product */
  function getInventoryYearPieceProduct() {
    setInventoryPieceProductLoading(true);
    fetchWrapper({
      endpoint_url: "dashboard/getPieceProduct",
      method: "GET",
      params: {
        year: inventoryYear.format("YYYY"),
        site_ID: siteSelected,
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        setInventoryPieceProduct(response.data.piece_product);
        setInventoryPieceProductLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({ message: response.message });
        setInventoryPieceProductLoading(false);
      },
    });
  }

  /** API to get base year revenue */
  function getBaseYearRevenue() {
    setBaseRevenueLoading(true);
    fetchWrapper({
      endpoint_url: "dashboard/getRevenue",
      method: "GET",
      params: {
        year: baseYear.format("YYYY"),
        site_ID: siteSelected,
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        setBaseRevenue(response.data.revenue);
        setBaseRevenueLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setBaseRevenueLoading(false);
      },
    });
  }

  /** API to get emission goal */
  function getEmissionGoal() {
    setEmissionGoalLoading(true);
    fetchWrapper({
      endpoint_url: "dashboard/getCarbonAccountingGoal",
      method: "GET",
      params: {
        year: inventoryYear.format("YYYY"),
        site_ID: siteSelected,
        type: "Carbon Emission",
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        setEmissionGoal(response.data.goal);
        setEmissionGoalLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setEmissionGoalLoading(false);
      },
    });
  }

  /** API to get revenue goal */
  function getRevenueGoal() {
    setRevenueTargetLoading(true);
    fetchWrapper({
      endpoint_url: "dashboard/getCarbonAccountingGoal",
      method: "GET",
      params: {
        year: inventoryYear.format("YYYY"),
        site_ID: siteSelected,
        type: "Revenue",
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        setRevenueTarget(response.data.goal);
        setRevenueTargetLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setRevenueTargetLoading(false);
      },
    });
  }

  /** API to get all emission scope data */
  function getEmissionData() {
    setEmissionDataLoading(true);
    fetchWrapper({
      endpoint_url: "dashboard/getEmissionData",
      method: "GET",
      params: {
        base_year: baseYear.format("YYYY-MM-DD"),
        inventory_year: inventoryYear.format("YYYY-MM-DD"),
        site_ID: siteSelected,
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        setEmissionScopeData(response.data);
        setEmissionDataLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setEmissionDataLoading(false);
      },
    });
  }

  /** API to get emission data summary */
  function getEmissionSummary() {
    setEmissionSummaryLoading(true);
    fetchWrapper({
      endpoint_url: "dashboard/getEmissionSummary",
      method: "GET",
      params: {
        base_year: baseYear.format("YYYY-MM-DD"),
        inventory_year: inventoryYear.format("YYYY-MM-DD"),
        site_ID: siteSelected,
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        setEmissionSummary(response.data);
        setEmissionSummaryLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setEmissionSummaryLoading(false);
      },
    });
  }

  /** API to get all abatement scope data */
  function getAbatementData() {
    setAbatementDataLoading(true);
    fetchWrapper({
      endpoint_url: "dashboard/getAbatementData",
      method: "GET",
      params: {
        base_year: baseYear.format("YYYY-MM-DD"),
        inventory_year: inventoryYear.format("YYYY-MM-DD"),
        site_ID: siteSelected,
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        setAbatementScopeData(response.data.scope_data);
        setAbatementSummary(response.data.summary);
        setAbatementDataLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setAbatementDataLoading(false);
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      getEmissionData();
      getEmissionSummary();
      getAbatementData();
    }
  }, [siteSelected, inventoryYear, baseYear]);

  useEffect(() => {
    if (siteSelected) {
      getInventoryYearRevenue();
      getRevenueGoal();
      getEmissionGoal();
      getInventoryYearTonneProduct();
      getInventoryYearPieceProduct();
    }
  }, [siteSelected, inventoryYear]);

  useEffect(() => {
    if (siteSelected) {
      getBaseYearRevenue();
    }
  }, [siteSelected, baseYear]);

  useEffect(() => {
    setIntensityGoal(emissionGoal / revenueTarget);
  }, [emissionGoal, revenueTarget]);

  useEffect(() => {
    setBaseYear(baseYearFilter);
    setInventoryYear(inventoryYearFilter);
  }, [filterPressed]);

  useEffect(() => {
    /** Summed emission for inventory year */
    let totalEmission = 0;
    /** Summed emission for base year */
    let baseEmission = 0;

    // Sum the summary data content
    // Summary data contains the sum from each inventory that matches the filter given
    emissionSummary
      .filter(
        (element) => element.InventoryYear === inventoryYear.year().toString(),
      )
      .forEach((value) => {
        totalEmission += value.TotalCO2e;
      });
    emissionSummary
      .filter((element) => element.InventoryYear === baseYear.year().toString())
      .forEach((value) => {
        baseEmission += value.TotalCO2e;
      });

    let totalAbatement = 0;
    let baseAbatement = 0;
    abatementScopeData
      .filter((element) => element.Year === inventoryYear.year().toString())
      .forEach((value) => {
        totalAbatement += value.Quantity * value.EmissionFactor;
      });
    abatementScopeData
      .filter((element) => element.Year === baseYear.year().toString())
      .forEach((value) => {
        baseAbatement += value.Quantity * value.EmissionFactor;
      });

    /** Calculate the difference between the base year and the inventory year */
    setNetEmission(totalEmission - totalAbatement);
    setNetBaseEmission(baseEmission - baseAbatement);

    setTotalEmissionCalculated(totalEmission);
    setTotalAbatementCalculated(totalAbatement);
  }, [emissionSummary, abatementScopeData]);

  return (
    <div>
      <Row gutter={4}>
        <Col
          xs={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 10 }}
          style={{
            height: screens.xl ? "84vh" : "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="p-1">
            <Card size="small">
              <Flex
                style={{ fontWeight: "bold" }}
                justify="space-between"
                align="center"
              >
                <Flex align="center">
                  <span>Year :</span>
                  <DatePicker
                    picker="year"
                    style={{ flexGrow: 1, marginLeft: "4px" }}
                    value={inventoryYearFilter}
                    onChange={(value) => {
                      if (value) {
                        if (baseYearFilter >= dayjs(value).startOf("year")) {
                          setBaseYearFilter(
                            dayjs(value).startOf("year").subtract(1, "year"),
                          );
                        }
                        setInventoryYearFilter(value);
                      }
                    }}
                  />
                </Flex>
                <Flex align="center">
                  <span>Previous Year :</span>
                  <DatePicker
                    picker="year"
                    style={{ marginLeft: "4px", flexGrow: "1" }}
                    value={baseYearFilter}
                    onChange={(value) => {
                      if (value) {
                        setBaseYearFilter(value);
                      }
                    }}
                    disabledDate={(date) => {
                      return (
                        date &&
                        date >= dayjs(inventoryYearFilter).startOf("year")
                      );
                    }}
                  />
                </Flex>
                <Button
                  disabled={loading}
                  icon={<SearchOutlined />}
                  onClick={onFilter}
                >
                  {screens.xs || (screens.sm && !screens.md) ? "Filter" : ""}
                </Button>
              </Flex>
            </Card>
          </div>

          <div className="p-1">
            <Row sm={24} gutter={8}>
              <Col sm={24} md={12}>
                <YoYCard
                  title={"Carbon Emission"}
                  precision={0}
                  unit={
                    <span
                      style={{
                        margin: "0px",
                        fontSize: "13px",
                        paddingLeft: "2px",
                      }}
                    >
                      tCO<sub>2</sub>e
                    </span>
                  }
                  currentQuantity={netEmission}
                  previousQuantity={netBaseEmission}
                  goalQuantity={emissionGoal}
                  loading={loading}
                />
              </Col>
              <Col sm={24} md={12} style={{ width: "100%" }}>
                <Card
                  loading={loading}
                  size="small"
                  title={
                    <Row>
                      <Col span={12}>
                        <h5 style={{ margin: "0px", fontSize: "17.6px" }}>
                          Carbon Intensity
                        </h5>
                      </Col>
                    </Row>
                  }
                  style={{ width: "100%" }}
                >
                  {!loading && (
                    <Carousel autoplay={false} dots={{ className: "carousel" }}>
                      <div>
                        <Row style={{ width: "100%", padding: "3px 10px" }}>
                          <span
                            style={{
                              margin: "0px",
                              fontSize: "12px",
                              fontWeight: "500",
                              paddingLeft: "2px",
                            }}
                          >
                            Per revenue
                          </span>
                        </Row>
                        <Row
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            paddingBottom: "0px",
                            textAlign: "left",
                            alignItems: "baseline",
                          }}
                          gutter={8}
                        >
                          <Col>
                            <h2
                              style={{
                                margin: "0px",
                                fontSize: "26px",
                                fontWeight: "600",
                              }}
                            >
                              {inventoryRevenue === 0
                                ? "-"
                                : (
                                    netEmission / inventoryRevenue
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 5,
                                    maximumFractionDigits: 5,
                                  })}
                            </h2>
                          </Col>
                          <Col>
                            <span style={{ margin: "0px", fontSize: "17.6px" }}>
                              tCO₂e/MYR
                            </span>
                          </Col>
                        </Row>
                        <Row
                          style={{
                            width: "100%",
                            padding: "0px 10px",
                            paddingBottom: "8px",
                            marginTop: "-8px",
                            textAlign: "left",
                          }}
                        >
                          <span style={{ margin: "0px", fontSize: "17.6px" }}>
                            &nbsp;
                          </span>
                        </Row>
                      </div>

                      <div>
                        <Row style={{ width: "100%", padding: "3px 10px" }}>
                          <span
                            style={{
                              margin: "0px",
                              fontSize: "12px",
                              fontWeight: "500",
                              paddingLeft: "2px",
                            }}
                          >
                            Per tonne of product
                          </span>
                        </Row>
                        <Row
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            paddingBottom: "0px",
                            textAlign: "left",
                            alignItems: "baseline",
                          }}
                          gutter={8}
                        >
                          <Col>
                            <h2
                              style={{
                                margin: "0px",
                                fontSize: "26px",
                                fontWeight: "600",
                              }}
                            >
                              {inventoryTonneProduct === 0
                                ? "-"
                                : (
                                    netEmission / inventoryTonneProduct
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 5,
                                    maximumFractionDigits: 5,
                                  })}
                            </h2>
                          </Col>
                          <Col>
                            <span style={{ margin: "0px", fontSize: "17.6px" }}>
                              tCO₂e/tonne of product
                            </span>
                          </Col>
                        </Row>
                        <Row
                          style={{
                            width: "100%",
                            padding: "0px 10px",
                            paddingBottom: "8px",
                            marginTop: "-8px",
                            textAlign: "left",
                          }}
                        >
                          <span style={{ margin: "0px", fontSize: "17.6px" }}>
                            &nbsp;
                          </span>
                        </Row>
                      </div>

                      <div>
                        <Row style={{ width: "100%", padding: "3px 10px" }}>
                          <span
                            style={{
                              margin: "0px",
                              fontSize: "12px",
                              fontWeight: "500",
                              paddingLeft: "2px",
                            }}
                          >
                            Per piece of product
                          </span>
                        </Row>
                        <Row
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            paddingBottom: "0px",
                            textAlign: "left",
                            alignItems: "baseline",
                          }}
                          gutter={8}
                        >
                          <Col>
                            <h2
                              style={{
                                margin: "0px",
                                fontSize: "26px",
                                fontWeight: "600",
                              }}
                            >
                              {inventoryPieceProduct === 0
                                ? "-"
                                : (
                                    netEmission / inventoryPieceProduct
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 5,
                                    maximumFractionDigits: 5,
                                  })}
                            </h2>
                          </Col>
                          <Col>
                            <span style={{ margin: "0px", fontSize: "17.6px" }}>
                              tCO₂e/piece of product
                            </span>
                          </Col>
                        </Row>
                        <Row
                          style={{
                            width: "100%",
                            padding: "0px 10px",
                            paddingBottom: "8px",
                            marginTop: "-8px",
                            textAlign: "left",
                          }}
                        >
                          <span style={{ margin: "0px", fontSize: "17.6px" }}>
                            &nbsp;
                          </span>
                        </Row>
                      </div>
                    </Carousel>
                  )}
                </Card>
              </Col>
            </Row>
          </div>

          <div
            className="p-1"
            style={{
              width: "100%",
              display: screens.xl ? "flex" : "block",
              flexDirection: "column",
              flexGrow: screens.xl ? 1 : 0,
              // height: "10px",
            }}
          >
            <OverviewCard
              title={"Overview"}
              year={inventoryYear.year()}
              emissionData={emissionSummary}
              abatementData={abatementScopeData}
              loading={loading}
            />
          </div>

          <div
            className="p-1"
            style={{
              width: "100%",
              flexGrow: screens.xl ? 1 : 0,
            }}
          >
            <PerformanceCard
              data={emissionSummary}
              abatementData={abatementSummary}
              loading={loading}
            />
          </div>
        </Col>
        <Col
          xs={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 8 }}
          style={{
            height: screens.xl ? "84vh" : "auto",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100%", // Ensure the container spans the full height
            }}
          >
            <div
              style={{
                width: "100%",
                padding: "4px",
                height: screens.xl ? "33.33%" : "auto",
              }}
            >
              <BreakdownCard
                title="Scope 1 - Direct Emissions"
                chartContainerName="scope1-chart"
                year={inventoryYear.year()}
                baseYear={baseYear.year()}
                emissionSummary={emissionSummary}
                emissionSummaryKeys={["Category1CO2e"]}
                emissionScopeData={emissionScopeData.filter(
                  (item) => item.Scope === 1,
                )}
                abatementData={abatementScopeData.filter(
                  (item) => item.Scope === 1,
                )}
                loading={loading}
              />
            </div>
            <div
              style={{
                width: "100%",
                padding: "4px",
                height: screens.xl ? "33.33%" : "auto",
              }}
            >
              <BreakdownCard
                title="Scope 2 - Indirect Emissions"
                chartContainerName="scope2-chart"
                year={inventoryYear.year()}
                baseYear={baseYear.year()}
                emissionSummary={emissionSummary}
                emissionSummaryKeys={["Category2CO2e"]}
                emissionScopeData={emissionScopeData.filter(
                  (item) => item.Scope === 2,
                )}
                abatementData={abatementScopeData.filter(
                  (item) => item.Scope === 2,
                )}
                loading={loading}
              />
            </div>
            <div
              style={{
                width: "100%",
                padding: "4px",
                height: screens.xl ? "33.33%" : "auto",
              }}
            >
              <BreakdownCard
                title="Scope 3 - Other Indirect Emissions"
                chartContainerName="scope3-chart"
                year={inventoryYear.year()}
                baseYear={baseYear.year()}
                emissionSummary={emissionSummary}
                emissionSummaryKeys={["Category3CO2e"]}
                emissionScopeData={emissionScopeData.filter(
                  (item) => item.Scope === 3,
                )}
                abatementData={abatementScopeData.filter(
                  (item) => item.Scope === 3,
                )}
                loading={loading}
              />
            </div>
          </div>
        </Col>
        <Col
          xs={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 6 }}
          style={{ width: "100%", height: screens.xl ? "84vh" : "auto" }}
        >
          <div
            style={{
              width: "100%",
              padding: "4px",
              height: screens.xl ? "45%" : "auto",
            }}
          >
            <RankingCard
              title="Ranking"
              emissionData={emissionScopeData}
              abatementData={abatementScopeData}
              year={inventoryYear.year()}
              loading={loading}
              totalEmission={totalEmissionCalculated}
              totalAbatement={totalAbatementCalculated}
            />
          </div>
          <div
            style={{
              width: "100%",
              padding: "4px",
              height: screens.xl ? "55%" : "auto",
            }}
          >
            <RECCard
              year={inventoryYear.year()}
              netEmission={netEmission}
              emissionSummary={emissionSummary}
              emissionScope2List={emissionScopeData.filter(
                (item) => item.Scope === 2,
              )}
              abatementList={abatementScopeData}
              abatementSummary={abatementSummary}
              gridEmissionFactor={GRID_EMISSION_FACTOR}
              loading={loading}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default DashboardPage;
